'use client'

import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material'

import { Badge } from '../library/badge'
import { Button } from '../library/button'
import { type FilterHeaderProps } from './filter-header-types'
import { Icon } from '@/icons/icon'
import Link from 'next/link'
import { MinMaxFilterSmall } from './min-max-filter'
import { RadioFilterSmall } from './radio-filter'
import { getAbsoluteUrl } from '@/utils/functions/url-utils'
import { usePathname, useRouter } from 'next/navigation'
import { type ReactNode, useState } from 'react'
import { useGetRedirectPath } from '@/components/utility/product-filters/product-filters'
import { usePaginationContext } from '../utility/pagination/pagination-context'
import { type GetRetailersQuery, type Minmax } from '@/lib/generated/graphql'
import { CheckboxFilterSmall } from './checkbox-filter'

const discountOptions = [
  {
    label: 'All Specials',
    value: 0,
  },
  {
    label: 'Over 20%',
    value: 20,
  },
  {
    label: 'Over 30%',
    value: 30,
  },
  {
    label: 'Over 40%',
    value: 40,
  },
  {
    label: 'Over 50%',
    value: 50,
  },
]

export const ClearAllButton = () => {
  const pathName = usePathname()
  return (
    <Link prefetch={false} href={getAbsoluteUrl(pathName)}>
      <div
        style={{ gridArea: 'right-bottom' }}
        className='mx-auto max-w-max flex-1 cursor-pointer place-self-center text-xs font-bold underline'
      >
        Clear All
      </div>
    </Link>
  )
}

const SubtractIcon = () => (
  <Icon size='s' color='dark' name='subtract-regular' variant='dark' />
)

const AddIcon = () => (
  <Icon size='s' color='dark' name='add-regular' variant='dark' />
)

const getRetailerNames = (retailersData: GetRetailersQuery | undefined) => {
  if (retailersData?.getRetailers.__typename === 'retailerList') {
    const retailers = retailersData.getRetailers?.items
    // eslint-disable-next-line @typescript-eslint/member-delimiter-style
    const retailerNames: Array<{ label: string; value: string }> = []
    if (retailers)
      for (const retailer of retailers)
        retailerNames.push({
          label: retailer?.retailer_displayName ?? '',
          value: retailer?.retailer_id ?? '',
        })

    return retailerNames
  }
  return []
}

export const MobileFilter = ({
  variant = 'product',
  selectedDiscount,
  selectedPrice,
  hideCategoriesFilters,
  hideRetailersFilters,
  hideBrandFilters,
  selectedCategories,
  selectedRetailers,
  selectedBrands,
  catalogueFilter,
  brandFilter,
  retailersOptions,
}: FilterHeaderProps & {
  catalogueFilter: ReactNode
  brandFilter: ReactNode
  retailersOptions?: GetRetailersQuery | undefined
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const { getRedirectPath } = useGetRedirectPath()
  const { setPage } = usePaginationContext()
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const handleClose = () => {
    setOpen(false)
  }
  const [expanded, setExpanded] = useState<string | false>(false)
  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false)
    }
  const handleDiscountFilterChange = (selected: string) => {
    const redirectPath = getRedirectPath(
      selected === '0' ? [] : [selected],
      'discount',
    )
    setPage(1)
    router.push(redirectPath, {
      scroll: false,
    })
  }

  const handlePriceFilterChange = (minMaxValues: Minmax) => {
    const redirectPath = getRedirectPath(
      [
        minMaxValues?.min?.toString() ?? '0',
        minMaxValues?.max?.toString() ?? '0',
      ],
      'price',
    )
    setPage(1)
    router.push(redirectPath, {
      scroll: false,
    })
  }

  return (
    <div
      className={`${open ? '' : 'sticky top-[150px] z-[49] box-border h-10 bg-white p-0 pt-2'} relative flex justify-between`}
    >
      <div
        className='cursor-pointer'
        onClick={() => {
          setOpen(true)
        }}
      >
        <Badge
          variant='dot'
          invisible={(selectedPrice?.length ?? 0) === 0}
          badgeContainerClassName='flex items-center'
        >
          <Icon
            size='s'
            color='light'
            name='filters-regular'
            className='ml-6 mr-2'
          />
          <span className='text-sm font-bold'>Filters</span>
        </Badge>
      </div>

      <ClearAllButton />
      {open && (
        <div
          role='presentation'
          className='fixed inset-0 z-[999] flex h-screen w-screen flex-col overflow-x-hidden rounded-none bg-white outline-none'
        >
          <div className='relative flex items-end justify-between border-b-4 border-primary bg-dark px-6 pb-6 pt-8'>
            <h3 className='text-h3 leading-h3 text-white'>Filters</h3>
            <Icon
              size='s'
              color='light'
              name='close-regular'
              variant='dark'
              onClick={handleClose}
            />
          </div>
          <div>
            {variant === 'product' && (
              <>
                <Accordion
                  elevation={0}
                  disableGutters
                  expanded={expanded === 'type'}
                  onChange={handleChange('type')}
                >
                  <AccordionSummary
                    className='pr-6'
                    expandIcon={
                      expanded === 'type' ? <SubtractIcon /> : <AddIcon />
                    }
                  >
                    <div className='my-0 ml-6 mr-2 text-sm'>Price</div>
                  </AccordionSummary>
                  <AccordionDetails className='ml-8'>
                    <MinMaxFilterSmall
                      title={'Price'}
                      minVal={
                        (selectedPrice?.length ?? 0) > 0
                          ? Number.parseInt(selectedPrice?.[0] as string)
                          : 1
                      }
                      maxVal={
                        (selectedPrice?.length ?? 0) > 0
                          ? Number.parseInt(selectedPrice?.[1] as string)
                          : 10_000
                      }
                      maxName='Max'
                      minName='Min'
                      data-testid='price-filter'
                      onFilterSelect={handlePriceFilterChange}
                    />
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  elevation={0}
                  disableGutters
                  expanded={expanded === 'discount'}
                  onChange={handleChange('discount')}
                >
                  <AccordionSummary
                    expandIcon={
                      expanded === 'discount' ? <SubtractIcon /> : <AddIcon />
                    }
                    className='pr-6'
                  >
                    <Badge
                      variant='dot'
                      invisible={
                        (selectedDiscount?.length ?? 0) === 0 ||
                        selectedDiscount?.[0] === '0'
                      }
                    >
                      <div className='my-0 ml-6 mr-2 text-sm'>Discount</div>
                    </Badge>
                  </AccordionSummary>
                  <AccordionDetails className='ml-8'>
                    <RadioFilterSmall
                      selected={
                        (selectedDiscount?.length ?? 0) > 0
                          ? selectedDiscount
                          : [0]
                      }
                      options={discountOptions}
                      onFilterSelect={handleDiscountFilterChange}
                    />
                  </AccordionDetails>
                </Accordion>

                {!hideBrandFilters && (
                  <Accordion
                    elevation={0}
                    disableGutters
                    expanded={expanded === 'brands'}
                    onChange={handleChange('brands')}
                  >
                    <AccordionSummary
                      expandIcon={
                        expanded === 'brands' ? <SubtractIcon /> : <AddIcon />
                      }
                      className='pr-6'
                    >
                      <Badge
                        variant='dot'
                        invisible={(selectedBrands?.length ?? 0) === 0}
                      >
                        <div className='my-0 ml-6 mr-2 text-sm'>Brands</div>
                      </Badge>
                    </AccordionSummary>
                    <AccordionDetails className='mx-6 my-0'>
                      {brandFilter}
                    </AccordionDetails>
                  </Accordion>
                )}
              </>
            )}

            {!hideCategoriesFilters && (
              <Accordion
                elevation={0}
                disableGutters
                expanded={expanded === 'categories'}
                onChange={handleChange('categories')}
              >
                <AccordionSummary
                  expandIcon={
                    expanded === 'categories' ? <SubtractIcon /> : <AddIcon />
                  }
                  className='pr-6'
                >
                  <Badge
                    variant='dot'
                    invisible={(selectedCategories?.length ?? 0) === 0}
                  >
                    <div className='my-0 ml-6 mr-2 text-sm'>Categories</div>
                  </Badge>
                </AccordionSummary>
                <AccordionDetails className='mx-6 my-0'>
                  {catalogueFilter}
                </AccordionDetails>
              </Accordion>
            )}
            {!hideRetailersFilters && (
              <Accordion
                elevation={0}
                disableGutters
                expanded={expanded === 'retailers'}
                onChange={handleChange('retailers')}
              >
                <AccordionSummary
                  expandIcon={
                    expanded === 'retailers' ? <SubtractIcon /> : <AddIcon />
                  }
                  className='pr-6'
                >
                  <Badge
                    variant='dot'
                    invisible={(selectedRetailers?.length ?? 0) === 0}
                  >
                    <div className='my-0 ml-6 mr-2 text-sm'>Retailers</div>
                  </Badge>
                </AccordionSummary>
                <AccordionDetails
                  style={{
                    margin: '0px 24px 0px 24px',
                  }}
                >
                  <CheckboxFilterSmall
                    options={getRetailerNames(retailersOptions)}
                    selected={selectedRetailers ?? []}
                    filterPath='retailers'
                  />
                </AccordionDetails>
              </Accordion>
            )}
          </div>

          <div className='bottom-6 flex flex-col items-center'>
            <ClearAllButton />
            <Button
              variant='contained'
              color='accent'
              className='ml-0 mt-4 h-10 w-[256px] border-none'
              onClick={handleClose}
            >
              Apply Filters
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
